import { useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { UUID } from '@/types/common';
import { UpdateWorkOrderOperationRequest, WorkOrderOperation } from '@/types/workOrders';
import { WorkOrderBaseState } from './workOrderBase';

export const useWorkOrderOperationManagement = (baseState: WorkOrderBaseState) => {
  const {
    loading,
    setLoading,
    workOrderOperations,
    setWorkOrderOperations,
    toast
  } = baseState;

  /**
   * Fetch Work Order Operations by Work Order ID
   */
  const fetchWorkOrderOperations = useCallback(async (workOrderId: UUID) => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('work_order_operations_with_details')
        .select('*')
        .eq('work_order_id', workOrderId)
        .order('sequence_no', { ascending: true });

      if (error) {
        console.error('Error fetching work order operations:', error);
        throw error;
      }

      // Map database column names to TypeScript interface field names
      const mappedData = data?.map(op => ({
        ...op,
        sequence: op.sequence_no,
        subcontracted: op.is_subcontracted,
        vendor_id: op.subcontractor_id,
        vendor_name: op.supplier_name, // View returns 'supplier_name' from suppliers table
        // Component information from routing_operations -> bom_components
        component_id: op.component_id,
        component_name: op.component_name,
        component_drawing_number: op.component_drawing_number,
      })) || [];

      setWorkOrderOperations(mappedData);
      return mappedData;
    } catch (error: any) {
      toast({
        title: 'Error fetching operations',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [setLoading, setWorkOrderOperations, toast]);

  /**
   * Start a Work Order Operation
   */
  const startOperation = useCallback(async (operationId: UUID) => {
    try {
      setLoading(true);
      
      // Get the operation to check its current status
      const { data: operation, error: fetchError } = await supabase
        .from('work_order_operations')
        .select('id, work_order_id, status, sequence_no')
        .eq('id', operationId)
        .single();
      
      if (fetchError) throw fetchError;
      
      if (!operation) {
        throw new Error('Operation not found');
      }
      
      if (operation.status !== 'Not Started') {
        throw new Error('Only operations in Not Started status can be started');
      }
      
      // If this is not the first operation, check if the previous operation is completed
      if (operation.sequence_no > 1) {
        const { data: prevOperation, error: prevError } = await supabase
          .from('work_order_operations')
          .select('status')
          .eq('work_order_id', operation.work_order_id)
          .eq('sequence_no', operation.sequence_no - 1)
          .single();
        
        if (!prevError && prevOperation && prevOperation.status !== 'Completed') {
          throw new Error('Previous operation must be completed first');
        }
      }
      
      // Update operation status
      const { data, error } = await supabase
        .from('work_order_operations')
        .update({ 
          status: 'In Progress',
          actual_start_time: new Date().toISOString()
        })
        .eq('id', operationId)
        .select()
        .single();
      
      if (error) throw error;
      
      // Ensure the work order is in progress
      await supabase
        .from('work_orders')
        .update({ 
          status: 'In Progress',
          actual_start_date: new Date().toISOString()
        })
        .eq('id', operation.work_order_id)
        .eq('status', 'Draft');
      
      toast({
        title: 'Operation started',
        description: 'Operation has been moved to In Progress.',
        variant: 'success',
      });
      
      // Refresh operations list
      if (workOrderOperations.some(op => op.id === operationId)) {
        await fetchWorkOrderOperations(data.work_order_id);
      }
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error starting operation',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkOrderOperations, setLoading, workOrderOperations, toast]);

  /**
   * Complete a Work Order Operation
   */
  const completeOperation = useCallback(async (operationId: UUID) => {
    try {
      setLoading(true);
      
      // Get the operation to check its current status
      const { data: operation, error: fetchError } = await supabase
        .from('work_order_operations')
        .select('id, work_order_id, status, qc_required, sequence_no')
        .eq('id', operationId)
        .single();
      
      if (fetchError) throw fetchError;
      
      if (!operation) {
        throw new Error('Operation not found');
      }
      
      if (operation.status !== 'In Progress') {
        throw new Error('Only operations in In Progress status can be completed');
      }
      
      // If QC is required, check if QC has passed
      if (operation.qc_required) {
        const { data: qcChecks, error: qcError } = await supabase
          .from('work_order_qc')
          .select('status')
          .eq('work_order_operation_id', operationId);
        
        if (!qcError && qcChecks) {
          const hasPassingQC = qcChecks.some(qc => qc.status === 'Pass');
          if (!hasPassingQC) {
            throw new Error('QC check must pass before completing this operation');
          }
        } else {
          throw new Error('QC check is required but has not been performed');
        }
      }
      
      // Update operation status
      const { data, error } = await supabase
        .from('work_order_operations')
        .update({ 
          status: 'Completed',
          actual_end_time: new Date().toISOString()
        })
        .eq('id', operationId)
        .select()
        .single();
      
      if (error) throw error;
      
      // Check if all operations are completed, if so, prompt to complete the work order
      const { data: operations, error: opsError } = await supabase
        .from('work_order_operations')
        .select('status')
        .eq('work_order_id', operation.work_order_id);
      
      if (!opsError && operations) {
        const allCompleted = operations.every(op => op.status === 'Completed');
        if (allCompleted) {
          toast({
            title: 'All operations completed',
            description: 'You can now complete the work order.',
            variant: 'info',
          });
        } else {
          // Start the next operation if it exists
          const { data: nextOperation, error: nextError } = await supabase
            .from('work_order_operations')
            .select('id')
            .eq('work_order_id', operation.work_order_id)
            .eq('sequence_no', operation.sequence_no + 1)
            .single();
          
          if (!nextError && nextOperation) {
            await startOperation(nextOperation.id);
          }
        }
      }
      
      toast({
        title: 'Operation completed',
        description: 'Operation has been completed successfully.',
        variant: 'success',
      });
      
      // Refresh operations list
      if (workOrderOperations.some(op => op.id === operationId)) {
        await fetchWorkOrderOperations(data.work_order_id);
      }
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error completing operation',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkOrderOperations, setLoading, startOperation, workOrderOperations, toast]);

  /**
   * Pause a Work Order Operation
   */
  const pauseOperation = useCallback(async (operationId: UUID) => {
    try {
      setLoading(true);

      // Get the operation to check its current status
      const { data: operation, error: fetchError } = await supabase
        .from('work_order_operations')
        .select('id, work_order_id, status, sequence_no')
        .eq('id', operationId)
        .single();

      if (fetchError) throw fetchError;

      if (!operation) {
        throw new Error('Operation not found');
      }

      if (operation.status !== 'In Progress') {
        throw new Error('Only operations in In Progress status can be paused');
      }

      // Get the associated job card for this operation
      const { data: jobCard, error: jobCardError } = await supabase
        .from('job_cards')
        .select('id, status, worker_id')
        .eq('work_order_operation_id', operationId)
        .in('status', ['In Progress'])
        .limit(1)
        .single();

      if (jobCardError && jobCardError.code !== 'PGRST116') {
        // PGRST116 = no rows returned - that's ok
        throw jobCardError;
      }

      const now = new Date().toISOString();

      // If there's an active job card, pause it (trigger will sync operation status)
      if (jobCard) {
        // Record activity log
        const { data: { user } } = await supabase.auth.getUser();

        await supabase
          .from('job_card_activities')
          .insert({
            job_card_id: jobCard.id,
            user_id: user?.id || jobCard.worker_id,
            activity_type: 'pause',
            description: 'Operation paused',
            metadata: {
              timestamp: now,
              status_before: jobCard.status,
              status_after: 'Paused'
            }
          });

        // Update job card status - trigger will update operation status to 'On Hold'
        const { error: updateJobCardError } = await supabase
          .from('job_cards')
          .update({
            status: 'Paused',
            updated_at: now
          })
          .eq('id', jobCard.id);

        if (updateJobCardError) throw updateJobCardError;

        // Update workstation status to Available
        const { data: jobWithWorkstation } = await supabase
          .from('job_cards')
          .select('workstation_id')
          .eq('id', jobCard.id)
          .single();

        if (jobWithWorkstation?.workstation_id) {
          await supabase
            .from('workstations')
            .update({
              status: 'Available',
              current_job_card_id: null,
              updated_at: now
            })
            .eq('id', jobWithWorkstation.workstation_id);
        }
      } else {
        // No active job card, directly update operation status to On Hold
        const { error: updateError } = await supabase
          .from('work_order_operations')
          .update({
            status: 'On Hold',
            updated_at: now
          })
          .eq('id', operationId);

        if (updateError) throw updateError;
      }

      toast({
        title: 'Operation paused',
        description: 'Operation has been put on hold.',
        variant: 'success',
      });

      // Refresh operations list
      if (workOrderOperations.some(op => op.id === operationId)) {
        await fetchWorkOrderOperations(operation.work_order_id);
      }

      return { success: true };
    } catch (error: any) {
      toast({
        title: 'Error pausing operation',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkOrderOperations, setLoading, workOrderOperations, toast]);

  /**
   * Reopen a completed operation
   */
  const reopenOperation = useCallback(async (operationId: UUID) => {
    try {
      setLoading(true);

      // Get the operation details
      const { data: operation, error: fetchError } = await supabase
        .from('work_order_operations')
        .select('*')
        .eq('id', operationId)
        .single();

      if (fetchError) throw fetchError;

      if (!operation) {
        throw new Error('Operation not found');
      }

      if (operation.status !== 'Completed') {
        throw new Error('Only completed operations can be reopened');
      }

      // Check if there are any subsequent operations that are in progress or completed
      const { data: subsequentOps, error: subsError } = await supabase
        .from('work_order_operations')
        .select('id, sequence_no, status')
        .eq('work_order_id', operation.work_order_id)
        .gt('sequence_no', operation.sequence_no)
        .in('status', ['In Progress', 'Completed']);

      if (!subsError && subsequentOps && subsequentOps.length > 0) {
        throw new Error('Cannot reopen this operation as subsequent operations have already started or completed');
      }

      // Get the associated job card
      const { data: jobCard, error: jobCardError } = await supabase
        .from('job_cards')
        .select('id, status')
        .eq('work_order_operation_id', operationId)
        .single();

      if (jobCardError && jobCardError.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw jobCardError;
      }

      // Update operation status back to In Progress
      const { data, error } = await supabase
        .from('work_order_operations')
        .update({
          status: 'In Progress',
          actual_end_time: null
        })
        .eq('id', operationId)
        .select()
        .single();

      if (error) throw error;

      // If there's a job card, update its status as well
      if (jobCard && jobCard.status === 'Completed') {
        await supabase
          .from('job_cards')
          .update({
            status: 'In Progress',
            actual_end_time: null,
            updated_at: new Date().toISOString()
          })
          .eq('id', jobCard.id);
      }

      toast({
        title: 'Operation reopened',
        description: 'Operation has been moved back to In Progress.',
        variant: 'success',
      });

      // Refresh operations list
      if (workOrderOperations.some(op => op.id === operationId)) {
        await fetchWorkOrderOperations(data.work_order_id);
      }

      return data;
    } catch (error: any) {
      toast({
        title: 'Error reopening operation',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkOrderOperations, setLoading, workOrderOperations, toast]);

  /**
   * Update a Work Order Operation
   */
  const updateWorkOrderOperation = useCallback(async (operation: UpdateWorkOrderOperationRequest) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('work_order_operations')
        .update(operation)
        .eq('id', operation.id)
        .select()
        .single();
      
      if (error) throw error;
      
      toast({
        title: 'Operation updated',
        description: 'Operation has been updated successfully.',
        variant: 'success',
      });
      
      // Refresh operations if we're looking at them
      if (workOrderOperations.some(op => op.id === operation.id)) {
        await fetchWorkOrderOperations(data.work_order_id);
      }
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error updating operation',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkOrderOperations, setLoading, workOrderOperations, toast]);

  /**
   * Create a QC check record
   */
  const createQCCheck = useCallback(async (qcCheck: {
    work_order_id: string;
    work_order_operation_id?: string;
    qc_point: string;
    inspector_id?: string;
    result: 'Pass' | 'Fail' | 'Rework' | 'Pending';
    comments?: string;
  }) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('work_order_qc')
        .insert([{
          ...qcCheck,
          inspection_date: new Date().toISOString()
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      toast({
        title: 'QC Check created',
        description: 'Quality check has been recorded successfully.',
        variant: 'success',
      });
      
      // Refresh operations if we're looking at them
      if (qcCheck.work_order_operation_id && 
          workOrderOperations.some(op => op.id === qcCheck.work_order_operation_id)) {
        const operation = workOrderOperations.find(op => op.id === qcCheck.work_order_operation_id);
        if (operation) {
          await fetchWorkOrderOperations(operation.work_order_id);
        }
      }
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error creating QC check',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkOrderOperations, setLoading, workOrderOperations, toast]);

  // Bulk start multiple operations
  const bulkStartOperations = useCallback(async (operations: WorkOrderOperation[]): Promise<void> => {
    setLoading(true);
    const results: { success: number; failed: number } = { success: 0, failed: 0 };

    for (const operation of operations) {
      try {
        const result = await startOperation(operation.id);
        if (result) {
          results.success++;
        } else {
          results.failed++;
        }
      } catch (error) {
        console.error(`Failed to start operation ${operation.id}:`, error);
        results.failed++;
      }
    }

    setLoading(false);

    if (results.failed > 0) {
      toast({
        title: 'Partial Success',
        description: `Started ${results.success} operations, ${results.failed} failed`,
        variant: 'warning',
      });
    }
  }, [startOperation, setLoading, toast]);

  // Bulk complete multiple operations
  const bulkCompleteOperations = useCallback(async (operations: WorkOrderOperation[]): Promise<void> => {
    setLoading(true);
    const results: { success: number; failed: number } = { success: 0, failed: 0 };

    for (const operation of operations) {
      try {
        const result = await completeOperation(operation.id);
        if (result) {
          results.success++;
        } else {
          results.failed++;
        }
      } catch (error) {
        console.error(`Failed to complete operation ${operation.id}:`, error);
        results.failed++;
      }
    }

    setLoading(false);

    if (results.failed > 0) {
      toast({
        title: 'Partial Success',
        description: `Completed ${results.success} operations, ${results.failed} failed`,
        variant: 'warning',
      });
    }
  }, [completeOperation, setLoading, toast]);

  return {
    loading,
    workOrderOperations,
    fetchWorkOrderOperations,
    startOperation,
    pauseOperation,
    completeOperation,
    reopenOperation,
    updateWorkOrderOperation,
    createQCCheck,
    bulkStartOperations,
    bulkCompleteOperations
  };
};
